'use client'

import { useEffect, useState } from 'react'
import { useVisitor } from '@/app/visitor/useVisitor'
import { nameScopeMessage, validNameKey, type NameScopeResult } from '@/contracts/namekeys'

type ValidationResult = NameScopeResult | { status: 'checking' | 'error'; scope: string }
const serverStatuses = new Set(['valid', 'missing-parent', 'sign-in-required', 'forbidden', 'invalid-name'])

/** A result belongs to both the typed prefix and the active visitor. Pending
 * and failed checks fail closed for creation without preventing lookup. */
export function useNameScopeValidation(scope: string) {
  const visitor = useVisitor()
  const identity = `${visitor.kind}:${visitor.messageId}:${visitor.codeKey}:${visitor.authMethod}:${visitor.isRestoring}`
  const key = `${identity}:${scope}`
  const [checked, setChecked] = useState<{ key: string; result: ValidationResult }>()
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    const onFocus = () => setRefresh(value => value + 1)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  useEffect(() => {
    if (!scope || !validNameKey(scope)) return
    let active = true
    const controller = new AbortController()
    setChecked(undefined)
    const timer = setTimeout(async () => {
      const timeout = setTimeout(() => controller.abort(), 10000)
      try {
        const response = await fetch(`/api/msgdesk/model?${new URLSearchParams({ action: 'check_name_scope', name_key: scope })}`, {
          credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        })
        const result = await response.json() as NameScopeResult
        if (!response.ok || !serverStatuses.has(result.status) || result.scope !== scope) throw new Error('Invalid scope response')
        if (active) setChecked({ key, result })
      } catch {
        if (active) setChecked({ key, result: { status: 'error', scope } })
      } finally {
        clearTimeout(timeout)
      }
    }, 250)
    return () => { active = false; clearTimeout(timer); controller.abort() }
  }, [scope, key, refresh])

  const result: ValidationResult = !validNameKey(scope)
    ? { status: 'invalid-name', scope }
    : checked?.key === key ? checked.result : { status: 'checking', scope }
  return {
    result,
    allowed: !scope || result.status === 'valid',
    message: scope ? nameScopeMessage(result) : '',
  }
}
